import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatEUR, formatDate, STATUS_LABELS, STATUS_COLORS, invoiceTitle } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

export default function RestaurantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const [data, setData] = useState(null);
  const [issueDialog, setIssueDialog] = useState(null); // {invoice, issue_date, expected_payment_date}
  const [payDialog, setPayDialog] = useState(null);     // {invoice, paid_date}

  const load = useCallback(async () => {
    const { data } = await api.get(`/restaurants/${id}/breakdown`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 font-mono text-sm text-zinc-500">Cargando…</div>;

  const { restaurant, totals, per_event, invoices } = data;

  const updateInvoice = async (inv, fields) => {
    await api.put(`/invoices/${inv.id}`, fields);
    toast.success("Factura actualizada");
    load();
  };

  const requestMail = (inv) => {
    const to = restaurant?.contact_email || "";
    const subject = `Solicitud de factura — ${invoiceTitle(inv)} — ${restaurant?.name || ""}`;
    const body = `Hola${restaurant?.contact_name ? " " + restaurant.contact_name : ""},

Te escribo para solicitar la factura correspondiente a:

· Concepto: ${invoiceTitle(inv)}
· Importe (IVA incluido): ${formatEUR(inv.amount_due)}
· Restaurante: ${restaurant?.name}
· Fecha de vencimiento: ${formatDate(inv.due_date)}

Por favor, emite la factura a nuestro nombre y envíala en cuanto puedas.

Muchas gracias.`;
    const params = new URLSearchParams({ view: "cm", fs: "1", to, su: subject, body });
    window.open(`https://mail.google.com/mail/?${params.toString()}`, "_blank");
    if (inv.status === "pending" || inv.status === "overdue") updateInvoice(inv, { status: "requested" });
  };

  const submitIssue = async () => {
    await updateInvoice(issueDialog.invoice, { status: "issued", issue_date: issueDialog.issue_date, expected_payment_date: issueDialog.expected_payment_date });
    setIssueDialog(null);
  };
  const submitPaid = async () => {
    await updateInvoice(payDialog.invoice, { status: "paid", paid_date: payDialog.paid_date });
    setPayDialog(null);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="restaurant-detail">
      <Link to="/restaurants" className="inline-flex items-center text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mb-4" data-testid="back-restaurants">
        <ArrowLeft className="h-3 w-3 mr-1" strokeWidth={1.5} /> Restaurantes
      </Link>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">{restaurant.city || "—"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">{restaurant.name}</h1>
          <p className="text-sm text-zinc-600 mt-2 font-mono">
            {restaurant.pax_per_session || 0} pax/turno · {formatEUR(restaurant.price_per_pax || 0)} · IVA {restaurant.iva_percent}%
          </p>
          {restaurant.contact_email && (
            <p className="text-xs text-zinc-500 font-mono mt-1">{restaurant.contact_email}</p>
          )}
        </div>
      </div>

      {restaurant.pax_per_session === 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-800" data-testid="warn-pax">
          ⚠ Este restaurante no tiene aforo por turno configurado. Edita el restaurante para que se calculen las facturas.
        </div>
      )}

      <div className="grid grid-cols-4 gap-4 mb-10">
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-eyebrow">Valor 90%</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(totals.value_90)}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-eyebrow">Anticipo (10%)</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(totals.anticipo_total)}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-eyebrow">Principal (90%)</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(totals.main_total)}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-eyebrow">Extras potenciales</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(totals.extra_potential)}</p>
        </div>
      </div>

      <section className="mb-10" data-testid="invoices-section">
        <h2 className="font-heading text-2xl mb-4">Facturas a emitir por el restaurante</h2>
        <div className="bg-white border border-zinc-200">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            <div className="col-span-3">Concepto</div>
            <div className="col-span-2">Vence</div>
            <div className="col-span-2">Importe</div>
            <div className="col-span-1">Estado</div>
            <div className="col-span-4 text-right">Acciones</div>
          </div>
          {invoices.length === 0 && (
            <div className="p-6 text-sm text-zinc-500">Sin facturas. Configura aforo y precio para generarlas.</div>
          )}
          {invoices.map((i) => (
            <div key={i.id} className="grid grid-cols-12 px-6 py-4 border-b border-zinc-100 items-center" data-testid={`invoice-row-${i.kind}`}>
              <div className="col-span-3">
                <p className="font-medium">{invoiceTitle(i)}</p>
                {i.adjusts_label && <p className="text-xs text-rose-700">Ajuste · {i.adjusts_label}</p>}
                {i.discount_amount > 0 && <p className="text-xs text-rose-700">−{formatEUR(i.discount_amount)} · {i.discount_note}</p>}
                {i.issue_date && <p className="text-xs text-zinc-500 font-mono">Emitida {formatDate(i.issue_date)}</p>}
                {i.paid_date && <p className="text-xs text-emerald-700 font-mono">Pagada {formatDate(i.paid_date)}</p>}
              </div>
              <div className="col-span-2 text-sm font-mono">{formatDate(i.due_date)}</div>
              <div className="col-span-2 font-mono">{formatEUR(i.amount_due)}</div>
              <div className="col-span-1">
                <span className={`text-xs px-2 py-1 border ${STATUS_COLORS[i.status]} uppercase tracking-wider`}>{STATUS_LABELS[i.status]}</span>
              </div>
              <div className="col-span-4 flex justify-end gap-2 flex-wrap">
                {isAdmin && (
                  <>
                    {(i.status === "pending" || i.status === "overdue") && (
                      <button onClick={() => requestMail(i)} data-testid={`req-${i.kind}`} className="inline-flex items-center text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50">
                        <Mail className="h-3 w-3 mr-1" strokeWidth={1.5} /> Solicitar
                      </button>
                    )}
                    {i.status === "requested" && (
                      <button onClick={() => requestMail(i)} data-testid={`req-again-${i.kind}`} className="inline-flex items-center text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50">
                        <Mail className="h-3 w-3 mr-1" strokeWidth={1.5} /> Reenviar
                      </button>
                    )}
                    {(i.status === "requested" || i.status === "pending" || i.status === "overdue") && (
                      <button onClick={() => setIssueDialog({ invoice: i, issue_date: new Date().toISOString().slice(0,10) })} data-testid={`issued-${i.kind}`} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 uppercase tracking-wider">Marcar emitida</button>
                    )}
                  </>
                )}
                {i.status === "issued" && (
                  <button onClick={() => setPayDialog({ invoice: i, paid_date: new Date().toISOString().slice(0,10) })} data-testid={`paid-${i.kind}`} className="text-xs px-2 py-1 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 uppercase tracking-wider">
                    <Check className="h-3 w-3 mr-1 inline" strokeWidth={1.5} /> Marcar pagada
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="events-breakdown">
        <h2 className="font-heading text-2xl mb-4">Desglose por evento ({per_event.length})</h2>
        <div className="bg-white border border-zinc-200">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            <div className="col-span-2">Fecha</div>
            <div className="col-span-2">Servicio</div>
            <div className="col-span-1">Aforo</div>
            <div className="col-span-2">Valor 90%</div>
            <div className="col-span-2">Anticipo</div>
            <div className="col-span-2">Principal</div>
            <div className="col-span-1">Estado</div>
          </div>
          {per_event.length === 0 && (
            <div className="p-6 text-sm text-zinc-500">Sin eventos.</div>
          )}
          {per_event.map((e) => (
            <div key={e.event_id} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-sm" data-testid={`ev-${e.event_id}`}>
              <div className="col-span-2 font-mono">{formatDate(e.date)}</div>
              <div className="col-span-2">{e.meal_type === "lunch" ? "Comida" : "Cena"} x{e.sessions}</div>
              <div className="col-span-1 font-mono">{e.pax_max}</div>
              <div className="col-span-2 font-mono">{formatEUR(e.value_90)}</div>
              <div className="col-span-2 font-mono text-zinc-600">{formatEUR(e.anticipo_share)}</div>
              <div className="col-span-2 font-mono text-zinc-600">{formatEUR(e.main_share)}</div>
              <div className="col-span-1 text-xs uppercase tracking-wider text-zinc-500">{e.status === "confirmed" ? "Conf." : "Prov."}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Issued dialog */}
      <Dialog open={!!issueDialog} onOpenChange={(v) => !v && setIssueDialog(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Marcar como emitida</DialogTitle>
            <DialogDescription>Introduce la fecha de la factura emitida por el restaurante.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider">Fecha de factura</Label>
            <Input
              type="date"
              value={issueDialog?.issue_date || ""}
              onChange={(e) => setIssueDialog({ ...issueDialog, issue_date: e.target.value })}
              className="rounded-none"
              data-testid="issue-date-input"
            />
          </div>
          <DialogFooter>
            <Button onClick={submitIssue} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="issue-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paid dialog */}
      <Dialog open={!!payDialog} onOpenChange={(v) => !v && setPayDialog(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Marcar como pagada</DialogTitle>
            <DialogDescription>Introduce la fecha de pago.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider">Fecha de pago</Label>
            <Input
              type="date"
              value={payDialog?.paid_date || ""}
              onChange={(e) => setPayDialog({ ...payDialog, paid_date: e.target.value })}
              className="rounded-none"
              data-testid="paid-date-input"
            />
          </div>
          <DialogFooter>
            <Button onClick={submitPaid} className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="paid-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
